/* Loads the two sources behind the Statistics page, side by side:
     • /statistics  — money (Revenue, Seller split, days, accounts) + Team rows
     • /chat-stats  — the AI's own activity counters (chats, asks, interrupts)
   Each source fails on its own: when one is down the page shows the other and a
   clear notice for the missing part — never a plausible-looking $0. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../api/client';
import type { ChatStatsResponse, StatisticsResponse } from '../../../api/client';

export interface StatisticsData {
  stats: StatisticsResponse | null;
  chat: ChatStatsResponse | null;
  statsError: string | null;
  chatError: string | null;
  loading: boolean;
  reload: () => void;
}

export function useStatisticsData(start: string, end: string, accountId: string | undefined, enabled: boolean): StatisticsData {
  const [stats, setStats] = useState<StatisticsResponse | null>(null);
  const [chat, setChat] = useState<ChatStatsResponse | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const load = useCallback(() => {
    if (!enabled) return;
    const n = ++seq.current;
    setLoading(true);
    const params = { start, end, accountId };
    Promise.allSettled([api.getStatistics(params), api.getChatStats(params)]).then(([s, c]) => {
      if (n !== seq.current) return; // a newer range won the race
      if (s.status === 'fulfilled') { setStats(s.value); setStatsError(null); }
      else { setStats(null); setStatsError((s.reason as Error)?.message || 'failed'); }
      if (c.status === 'fulfilled') { setChat(c.value); setChatError(null); }
      else { setChat(null); setChatError((c.reason as Error)?.message || 'failed'); }
      setLoading(false);
    });
  }, [start, end, accountId, enabled]);

  useEffect(() => { load(); }, [load]);

  return { stats, chat, statsError, chatError, loading, reload: load };
}
